/**
 * Shared PDP gallery helpers (images + Shopify hosted / external video).
 * Used by rs-pdp.js (watch) and rs-pdp-bezel.js (bezel).
 */
(function (global) {
  "use strict";

  const PLAY_ICON =
    '<svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';

  const isVideo = (item) =>
    item?.mediaType === "video" || item?.mediaType === "external_video";

  const mapProductImages = (product) => {
    if (!product?.images?.length) return [];
    return product.images.map((img, i) => ({
      id: i === 0 ? "hero" : String(img.id),
      src: img.src,
      thumb: img.thumb || img.src,
      alt: img.alt || product.title || product.label || "",
      mediaType: img.mediaType || "image",
      sources: img.sources || [],
      embedUrl: img.embedUrl || "",
    }));
  };

  const buildVideoMedia = (item, autoplay, forcePlay = false) => {
    const wrap = document.createElement("div");
    wrap.className = "gallery-media gallery-media--video";

    if (item.mediaType === "external_video") {
      const iframe = document.createElement("iframe");
      const join = item.embedUrl.includes("?") ? "&" : "?";
      if (autoplay) {
        iframe.src = `${item.embedUrl}${join}autoplay=1&mute=1&muted=1&loop=1&playsinline=1`;
      } else if (forcePlay) {
        iframe.src = `${item.embedUrl}${join}autoplay=1&mute=1&muted=1&playsinline=1`;
      } else {
        iframe.src = item.embedUrl;
      }
      iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture");
      iframe.setAttribute("allowfullscreen", "");
      iframe.loading = "lazy";
      iframe.title = item.alt || "Product video";
      wrap.appendChild(iframe);
      return wrap;
    }

    const video = document.createElement("video");
    video.playsInline = true;
    video.preload = "metadata";
    if (item.src) video.poster = item.src;
    (item.sources || []).forEach((s) => {
      const source = document.createElement("source");
      source.src = s.url;
      source.type = s.mimeType;
      video.appendChild(source);
    });

    if (autoplay) {
      video.muted = true;
      video.autoplay = true;
      video.loop = true;
    } else if (forcePlay) {
      video.controls = true;
      video.play();
    } else {
      const playBtn = document.createElement("button");
      playBtn.type = "button";
      playBtn.className = "gallery-media__play";
      playBtn.setAttribute("aria-label", `Play ${item.alt || "video"}`);
      playBtn.innerHTML = PLAY_ICON;
      playBtn.addEventListener("click", () => {
        video.controls = true;
        video.play();
        playBtn.remove();
      });
      wrap.appendChild(playBtn);
    }

    wrap.insertBefore(video, wrap.firstChild);
    return wrap;
  };

  /**
   * @param {HTMLElement|null} fig - #gallery-hero figure
   * @param {object} item - gallery item
   * @param {{ autoplay?: boolean, animate?: boolean, forcePlay?: boolean }} [opts]
   */
  const renderHeroMedia = (fig, item, opts = {}) => {
    if (!fig || !item) return;
    const autoplay = Boolean(opts.autoplay);
    const animate = opts.animate !== false;
    const forcePlay = Boolean(opts.forcePlay);

    if (isVideo(item)) {
      fig.innerHTML = "";
      fig.appendChild(buildVideoMedia(item, autoplay, forcePlay));
      return;
    }

    let img = fig.querySelector("#hero-image");
    if (!img) {
      fig.innerHTML =
        '<img id="hero-image" width="1200" height="1200" decoding="async" alt="" />';
      img = fig.querySelector("#hero-image");
    }
    if (!item.src || !img) return;
    if (animate) img.classList.add("is-swapping");
    img.src = item.src;
    img.alt = item.alt || "";
    if (animate) {
      requestAnimationFrame(() => img.classList.remove("is-swapping"));
    }
  };

  global.RsPdpGallery = {
    PLAY_ICON,
    isVideo,
    mapProductImages,
    buildVideoMedia,
    renderHeroMedia,
  };
})(window);
